import React, { useState, useEffect } from "react";
import "./Sponsors.css";

function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentSponsor, setCurrentSponsor] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    logo: null,
    website: "",
    contactName: "",
    contactPhone: "",
    contactEmail: ""
  });

  // Get auth token from localStorage
  const getAuthToken = () => {
    return localStorage.getItem('token') || localStorage.getItem('authToken');
  };

  // Fetch all sponsors
  const fetchSponsors = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await fetch("https://api.asterickx.com/api/Sponsors", {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setSponsors(data);
    } catch (error) {
      console.error("Error fetching sponsors:", error);
      alert("Failed to load sponsors. Please check your authentication.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSponsors();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      logo: file
    }));
  };

  const handleAddNew = () => {
    setEditMode(false);
    setCurrentSponsor(null);
    setFormData({
      name: "",
      logo: null,
      website: "",
      contactName: "",
      contactPhone: "",
      contactEmail: ""
    });
    setShowModal(true);
  };

  const handleEdit = (sponsor) => {
    setEditMode(true);
    setCurrentSponsor(sponsor);
    setFormData({
      name: sponsor.name || "",
      logo: null,
      website: sponsor.website || "",
      contactName: sponsor.contactName || "",
      contactPhone: sponsor.contactPhone || "",
      contactEmail: sponsor.contactEmail || ""
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      alert("Name is required");
      return;
    }
    
    const token = getAuthToken();
    
    if (!token) {
      alert("Authentication token not found. Please log in again.");
      return;
    }
    
    const formDataToSend = new FormData();
    formDataToSend.append("Name", formData.name);
    if (formData.logo) {
      formDataToSend.append("Logo", formData.logo);
    }
    formDataToSend.append("Website", formData.website);
    formDataToSend.append("ContactName", formData.contactName);
    formDataToSend.append("ContactPhone", formData.contactPhone);
    formDataToSend.append("ContactEmail", formData.contactEmail);

    try {
      let response;
      if (editMode && currentSponsor) {
        response = await fetch(
          `https://api.asterickx.com/api/Sponsors/${currentSponsor.id}`,
          {
            method: "PUT",
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formDataToSend
          }
        );
      } else {
        response = await fetch(
          "https://api.asterickx.com/api/Sponsors",
          {
            method: "POST",
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formDataToSend
          }
        );
      }

      if (response.ok) {
        alert(editMode ? "Sponsor updated successfully!" : "Sponsor added successfully!");
        setShowModal(false);
        fetchSponsors();
      } else {
        const errorText = await response.text();
        console.error("Error response:", errorText);
        alert(`Failed to save sponsor: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error saving sponsor:", error);
      alert("An error occurred while saving");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this sponsor?")) {
      return;
    }

    try {
      const token = getAuthToken();
      const response = await fetch(
        `https://api.asterickx.com/api/Sponsors/${id}`,
        {
          method: "DELETE",
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        alert("Sponsor deleted successfully!");
        fetchSponsors();
      } else {
        alert("Failed to delete sponsor");
      }
    } catch (error) {
      console.error("Error deleting sponsor:", error);
      alert("An error occurred while deleting");
    }
  };

  const filteredSponsors = sponsors.filter(sponsor =>
    sponsor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sponsor.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sponsor.contactEmail?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="sp-container">
      {/* Header Section */}
      <div className="sp-header-section">
        <h1 className="sp-page-title">Sponsors Management</h1>
        <div className="sp-right-controls">
          <div className="sp-search-container">
            <input
              type="text"
              placeholder="Search sponsors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="sp-search-input"
            />
            <span className="sp-search-icon">🔍</span>
          </div>
          <button className="sp-add-btn" onClick={handleAddNew}>
            <span className="sp-btn-icon">+</span>
            Add New Sponsor
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="sp-text-center">
          <div className="sp-loader"></div>
          <p>Loading sponsors...</p>
        </div>
      ) : (
        <div className="sp-table-container">
          <table className="sp-table">
            <thead>
              <tr>
                <th>Logo</th>
                <th>Name</th>
                <th>Website</th>
                <th>Contact Name</th>
                <th>Phone Number</th>
                <th>Email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSponsors.length === 0 ? (
                <tr>
                  <td colSpan="7" className="sp-text-center">
                    No sponsors found
                  </td>
                </tr>
              ) : (
                filteredSponsors.map((sponsor) => (
                  <tr key={sponsor.id}>
                    <td>
                      <div className="sp-logo-cell">
                        {sponsor.logo ? (
                          <img src={sponsor.logo} alt={sponsor.name} className="sp-logo-img" />
                        ) : (
                          <div className="sp-logo-placeholder">📷</div>
                        )}
                      </div>
                    </td>
                    <td>{sponsor.name}</td>
                    <td>
                      {sponsor.website ? (
                        <a href={sponsor.website} target="_blank" rel="noopener noreferrer" className="sp-link">
                          {sponsor.website}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{sponsor.contactName || "-"}</td>
                    <td>
                      {sponsor.contactPhone ? (
                        <a href={`tel:${sponsor.contactPhone}`} className="sp-phone-link">
                          {sponsor.contactPhone}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{sponsor.contactEmail || "-"}</td>
                    <td>
                      <div className="sp-action-buttons">
                        <button className="sp-edit-btn" onClick={() => handleEdit(sponsor)}>
                          ✏️ Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="sp-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="sp-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-header">
              <h3>{editMode ? "Edit Sponsor" : "Add New Sponsor"}</h3>
              <button className="sp-close-btn" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <div className="sp-modal-body">
              <div className="sp-form-grid">
                <div className="sp-form-group sp-full-width">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="sp-form-group sp-full-width">
                  <label>Logo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  {editMode && currentSponsor?.logo && !formData.logo && (
                    <div className="sp-current-logo">
                      <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Current Logo:</p>
                      <img 
                        src={currentSponsor.logo} 
                        alt="Current logo" 
                        style={{ 
                          maxWidth: '200px', 
                          maxHeight: '100px', 
                          objectFit: 'contain',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          padding: '8px'
                        }} 
                      />
                      <p style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                        Upload a new file to replace
                      </p>
                    </div>
                  )}
                  {formData.logo && (
                    <div className="sp-current-logo">
                      <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>New Logo Selected:</p>
                      <p style={{ fontSize: '11px', color: '#333' }}>{formData.logo.name}</p>
                    </div>
                  )}
                </div>

                <div className="sp-form-group sp-full-width">
                  <label>Website</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                  />
                </div>

                <div className="sp-form-group">
                  <label>Contact Name</label>
                  <input
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="sp-form-group">
                  <label>Contact Phone</label>
                  <input
                    type="tel"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="sp-form-group sp-full-width">
                  <label>Contact Email</label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="sp-form-actions">
                <button className="sp-cancel-btn" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button className="sp-submit-btn" onClick={handleSubmit}>
                  {editMode ? "Update" : "Add"} Sponsor
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Sponsors;